import siteJson from '../content/site.json';
import legalManifest from '../content/legal/manifest.json';
import privacyRaw from '../content/legal/privacy.md?raw';
import consentRaw from '../content/legal/consent.md?raw';
import cookiesRaw from '../content/legal/cookies.md?raw';

export interface Price {
  display: string;
  amountRub: number;
  unit: string;
}

export interface Service {
  id: string;
  title: string;
  objective: string;
  result: string;
  format: string;
  included: string[];
  excluded: string[];
  estimateFactors: string[];
  price: Price;
}

export interface MapEmbed {
  yandexMapUrl?: string;
}

export interface LegalBlock {
  type: 'heading' | 'paragraph' | 'list';
  text?: string;
  level?: number;
  id?: string;
  items?: string[];
}

export interface LegalDocument {
  slug: 'privacy' | 'consent' | 'cookies';
  title: string;
  revision: string;
  effectiveDate: string;
  status: 'release-candidate';
  owner: string;
  blocks: LegalBlock[];
}

export const site = siteJson as typeof siteJson & { mapEmbed?: MapEmbed };
export const services = siteJson.services as Service[];

const slugify = (value: string) =>
  value
    .toLocaleLowerCase('ru-RU')
    .replace(/[^a-zа-яё0-9]+/giu, '-')
    .replace(/^-|-$/g, '');

function parseLegal(slug: LegalDocument['slug'], raw: string): LegalDocument {
  const normalized = raw.replace(/\r\n/g, '\n');
  const match = normalized.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) throw new Error(`Legal document ${slug} has no valid front matter`);

  const metadata = Object.fromEntries(
    match[1].split('\n').map((line) => {
      const separator = line.indexOf(':');
      return [line.slice(0, separator).trim(), line.slice(separator + 1).trim()];
    }),
  );

  const blocks: LegalBlock[] = [];
  const lines = match[2].trim().split('\n');
  let paragraph: string[] = [];
  let list: string[] = [];
  const flush = () => {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }
    if (list.length) {
      blocks.push({ type: 'list', items: list });
      list = [];
    }
  };

  for (const line of lines) {
    const heading = line.match(/^(#{2,3})\s+(.+)$/);
    if (heading) {
      flush();
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2], id: slugify(heading[2]) });
    } else if (line.startsWith('- ')) {
      if (paragraph.length) flush();
      list.push(line.slice(2));
    } else if (!line.trim()) {
      flush();
    } else {
      if (list.length) flush();
      paragraph.push(line.trim());
    }
  }
  flush();

  const manifestEntry = legalManifest.documents.find((entry) => entry.slug === slug);
  if (!manifestEntry || manifestEntry.revision !== metadata.revision) {
    throw new Error(`Legal manifest mismatch for ${slug}`);
  }

  return {
    slug,
    title: metadata.title,
    revision: metadata.revision,
    effectiveDate: metadata.effectiveDate,
    status: metadata.status as LegalDocument['status'],
    owner: metadata.owner,
    blocks,
  };
}

export const legalDocuments: Record<LegalDocument['slug'], LegalDocument> = {
  privacy: parseLegal('privacy', privacyRaw),
  consent: parseLegal('consent', consentRaw),
  cookies: parseLegal('cookies', cookiesRaw),
};

export const consentRevision = legalDocuments.consent.revision;
